import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Building2, Plus, Edit2, Trash2, Phone, Mail, Package, Save, X, Search } from 'lucide-react';
import { formatNzPhone } from '@/lib/formatters';

export default function SuppliersPage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editSupplier, setEditSupplier] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/suppliers?search=${search}`);
      setSuppliers(data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [api, search]);

  useEffect(() => { load(); }, [load]);

  const saveSupplier = async (form) => {
    if (editSupplier?._id) {
      await api.put(`/suppliers/${editSupplier._id}`, form);
    } else {
      await api.post('/suppliers', form);
    }
    setShowForm(false);
    setEditSupplier(null);
    load();
  };

  const deleteSupplier = async (id) => {
    if (!window.confirm('Delete this supplier? Products referencing it will keep the name.')) return;
    await api.delete(`/suppliers/${id}`);
    load();
  };

  return (
    <div data-testid="suppliers-page" className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 font-[Manrope]">Suppliers</h1>
          <p className="text-sm text-slate-500 mt-1 font-medium"><span className="font-mono-num font-bold text-slate-700">{suppliers.length}</span> {suppliers.length === 1 ? 'supplier' : 'suppliers'}</p>
        </div>
        <button data-testid="add-supplier-btn" onClick={() => { setEditSupplier(null); setShowForm(true); }} className="flex items-center gap-2 px-4 py-2 bg-[#D35400] text-white rounded-md text-sm font-semibold hover:bg-[#A04000] shadow-sm hover:shadow-md hover:shadow-orange-500/20 transition-all">
          <Plus size={16} strokeWidth={2.5} /> Add Supplier
        </button>
      </div>

      <div className="relative max-w-sm">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input data-testid="supplier-search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search suppliers..." className="w-full pl-9 pr-3 py-2 border border-[#E2E2DF] rounded-md text-sm" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {suppliers.map(s => {
          const initials = (s.name || '?').split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase() || '?';
          return (
            <div key={s._id} className="bg-white border border-[#E2E2DF] rounded-md p-5 card-lift relative overflow-hidden">
              <div className="absolute inset-x-0 top-0 h-[3px] bg-[#D35400]" />
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <div className="w-11 h-11 rounded-md bg-gradient-to-br from-[#D35400]/15 to-[#D35400]/5 flex items-center justify-center flex-shrink-0 ring-1 ring-[#D35400]/10">
                    <span className="text-xs font-bold text-[#D35400] font-[Manrope]">{initials}</span>
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-sm font-bold text-slate-800 font-[Manrope] truncate">{s.name}</h3>
                    {s.code && <p className="text-xs text-slate-400 font-mono-num">Code: {s.code}</p>}
                  </div>
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  <button data-testid={`edit-supplier-${s._id}`} onClick={() => { setEditSupplier(s); setShowForm(true); }} className="p-1.5 text-slate-400 hover:text-[#D35400] hover:bg-orange-50 rounded transition-colors"><Edit2 size={14} /></button>
                  <button onClick={() => deleteSupplier(s._id)} className="p-1.5 text-slate-400 hover:text-rose-500 hover:bg-rose-50 rounded transition-colors"><Trash2 size={14} /></button>
                </div>
              </div>
              <div className="space-y-1 mt-3">
                {s.contact_person && <div className="text-xs text-slate-700 font-medium">{s.contact_person}</div>}
                {s.email && <div className="flex items-center gap-1.5 text-xs text-slate-500 truncate"><Mail size={11} className="flex-shrink-0" />{s.email}</div>}
                {s.phone && <div className="flex items-center gap-1.5 text-xs text-slate-500 font-mono-num"><Phone size={11} className="flex-shrink-0" />{s.phone}</div>}
              </div>
              <div className="mt-3 pt-3 border-t border-[#E2E2DF] flex items-center justify-between">
                <span className="text-[10px] font-bold uppercase tracking-[0.18em] px-2 py-1 rounded-full bg-orange-50 text-[#D35400] ring-1 ring-orange-100"><Package size={10} className="inline mr-1" /><span className="font-mono-num">{s.product_count || 0}</span> products</span>
                <button onClick={() => navigate(`/materials?supplier=${s.name}`)} className="text-xs text-[#D35400] hover:text-[#A04000] font-semibold transition-colors">View →</button>
              </div>
            </div>
          );
        })}
        {suppliers.length === 0 && <div className="col-span-full text-center py-16"><p className="text-sm text-slate-400 mb-1">No suppliers found</p><p className="text-xs text-slate-300">Try adjusting your search</p></div>}
      </div>

      {showForm && (
        <SupplierFormModal
          supplier={editSupplier}
          onSave={saveSupplier}
          onClose={() => { setShowForm(false); setEditSupplier(null); }}
        />
      )}
    </div>
  );
}

function SupplierFormModal({ supplier, onSave, onClose }) {
  const [form, setForm] = useState(supplier || { name: '', code: '', contact_person: '', email: '', phone: '', fax: '', address: '', rtr_code: '', notes: '', active: true });
  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
        <h2 className="text-lg font-bold text-[#2C2C2C] mb-4">{supplier?._id ? 'Edit Supplier' : 'Add Supplier'}</h2>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><label className="text-xs font-medium text-slate-500 block mb-1">Name</label><input value={form.name} onChange={e => update('name', e.target.value)} className="w-full text-sm border border-[#E2E2DF] rounded-lg px-3 py-2" placeholder="Roofing Industries" /></div>
            <div><label className="text-xs font-medium text-slate-500 block mb-1">Code</label><input value={form.code} onChange={e => update('code', e.target.value)} className="w-full text-sm border border-[#E2E2DF] rounded-lg px-3 py-2" /></div>
            <div><label className="text-xs font-medium text-slate-500 block mb-1">RTR Code</label><input value={form.rtr_code} onChange={e => update('rtr_code', e.target.value)} className="w-full text-sm border border-[#E2E2DF] rounded-lg px-3 py-2" /></div>
            <div className="col-span-2"><label className="text-xs font-medium text-slate-500 block mb-1">Contact Person</label><input value={form.contact_person} onChange={e => update('contact_person', e.target.value)} className="w-full text-sm border border-[#E2E2DF] rounded-lg px-3 py-2" /></div>
            <div><label className="text-xs font-medium text-slate-500 block mb-1">Email</label><input value={form.email} onChange={e => update('email', e.target.value)} className="w-full text-sm border border-[#E2E2DF] rounded-lg px-3 py-2" /></div>
            <div><label className="text-xs font-medium text-slate-500 block mb-1">Phone</label><input value={form.phone} onChange={e => update('phone', e.target.value)} onBlur={() => update('phone', formatNzPhone(form.phone || ''))} placeholder="(07) 855 1234" className="w-full text-sm border border-[#E2E2DF] rounded-lg px-3 py-2" /></div>
            <div><label className="text-xs font-medium text-slate-500 block mb-1">Fax</label><input value={form.fax} onChange={e => update('fax', e.target.value)} className="w-full text-sm border border-[#E2E2DF] rounded-lg px-3 py-2" /></div>
            <div><label className="text-xs font-medium text-slate-500 block mb-1">Address</label><input value={form.address} onChange={e => update('address', e.target.value)} className="w-full text-sm border border-[#E2E2DF] rounded-lg px-3 py-2" /></div>
            <div className="col-span-2"><label className="text-xs font-medium text-slate-500 block mb-1">Notes</label><textarea value={form.notes} onChange={e => update('notes', e.target.value)} className="w-full text-sm border border-[#E2E2DF] rounded-lg px-3 py-2" rows={2} /></div>
          </div>
        </div>
        <div className="flex gap-3 mt-5">
          <button onClick={() => onSave(form)} className="flex-1 px-4 py-2 bg-[#D35400] text-white rounded-lg text-sm font-medium hover:bg-[#A04000]"><Save size={14} className="inline mr-1" />Save</button>
          <button onClick={onClose} className="px-4 py-2 border border-[#E2E2DF] rounded-lg text-sm text-slate-600 hover:bg-slate-50">Cancel</button>
        </div>
      </div>
    </div>
  );
}
